
import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

const mapsScriptSelector = 'script[data-google-maps-loader="true"]';

const withTimeout = <T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> => {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => reject(new Error(message)), timeoutMs);

    promise.then(
      result => {
        window.clearTimeout(timeout);
        resolve(result);
      },
      error => {
        window.clearTimeout(timeout);
        reject(error);
      }
    );
  });
};

const loadGoogleMapsScript = (apiKey: string): Promise<void> => {
  if (!apiKey) return Promise.resolve();

  const win = window as any;

  if (win.google?.maps?.importLibrary) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(mapsScriptSelector);

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Google Maps script failed to load.')), { once: true });
      return;
    }

    const script = document.createElement('script');

    script.dataset.googleMapsLoader = 'true';
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async&v=weekly`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Google Maps script failed to load.'));

    document.head.appendChild(script);
  });
};

const initializeGoogleMaps = async (): Promise<void> => {
  await withTimeout(loadGoogleMapsScript(environment.googleMapsApiKey), 8000, 'Google Maps script load timeout.');

  const maps = (window as any).google?.maps;

  if (!maps?.importLibrary) return;

  await Promise.all([
    maps.importLibrary('maps'),
    maps.importLibrary('places')
  ]);
};

const bootstrap = async (): Promise<void> => {
  if (environment.production) {
    enableProdMode();
  }

  try {
    await initializeGoogleMaps();
  } catch (error) {
    console.warn('Google Maps initialization failed; continuing app bootstrap.', error);
  }

  await platformBrowserDynamic().bootstrapModule(AppModule);
};

bootstrap().catch(err => console.error(err));
